import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  BicepsFlexed,
  Boxes,
  Building,
  Calculator,
  Check,
  CircleQuestionMark,
  Clock,
  Factory,
  House,
  Mail,
  MapPin,
  Menu,
  Phone,
  ShieldHalf,
  Truck,
  UserPen,
  Wallet,
} from "lucide-react";
import type { ComponentProps, CSSProperties } from "react";
import { cloneElement, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import type { Entries, ReadonlyTuple } from "type-fest";

import { CalculatorCard, CalculatorCardSkeleton } from "./calculator";
import { ConsentSummary } from "./consent-summary";
import { MyOrders } from "./order";
import styles from "./page.module.css";
import { ReviewsSectionContent } from "./review";
import { ServiceCard, ServiceCardSkeleton } from "./service";
import { WriteToUsForm } from "./write-to-us";

import { COMPANY_INFO, COMPANY_INFO_CONTACT_LINKS, Logo } from "~/entities/company";
import { useDi } from "~/entities/di";
import { LanguageSwitcher } from "~/entities/i18n_";
import type { OrderEntity } from "~/entities/order";
import { MyOrdersStore } from "~/entities/order";
import { ServiceQuery } from "~/entities/service";
import { CreationDialog as OrderCreationDialog } from "~/features/order";
import { ServiceDetailsDialog } from "~/features/service";
import { RetrieveFrom } from "~/pages/order-printout/@x/home";
import { getRandomElement, range } from "~/shared/arr";
import qrCode from "~/shared/assets/qr-code.jpg";
import { combine } from "~/shared/fp";
import {
  DayOfWeek,
  formatCurrency,
  getNameOfDayOfWeek,
  PartOfSpeech,
  Tk,
  TranslationParams,
  useTranslation2,
} from "~/shared/i18n";
import { HomeSectionName } from "~/shared/routing";
import {
  capitalize,
  COLON,
  COLON_WITH_SPACE,
  COPYRIGHT,
  DASH,
  EMPTY,
  HASH,
  PERIOD,
  QUESTION_MARK,
  SPACE,
} from "~/shared/str";
import { isObject } from "~/shared/type-guard";
import { Badge } from "~/shared/ui/badge";
import { Button } from "~/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/shared/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/shared/ui/carousel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";
import { QueryError } from "~/shared/ui/query-error";
import { Separator } from "~/shared/ui/separator";
import { cn } from "~/shared/ui/utils";

function HomePage() {
  const {
    translation: { t, i18n },
    postproc: { tc, ts, tsp },
  } = useTranslation2();
  const navigate = useNavigate();
  const { serviceService, eventBus } = useDi();

  const {
    data: services,
    isPending: isServicesPending,
    isError: isServicesError,
    refetch: refetchServices,
  } = ServiceQuery.useGetAll(serviceService);

  const servicesWithIcons = useMemo(
    () =>
      services?.map((service) => ({
        ...service,
        Icon: getRandomElement(ICONS)!,
      })) ?? [],
    [services],
  );

  const [orderCreationDialogProps, setOrderCreationDialogProps] = useState<
    Pick<
      ComponentProps<typeof OrderCreationDialog>,
      "services" | "selectedServices" | "defaultValues"
    >
  >({});
  const [isOrderCreationDialogOpen, setOrderCreationDialogOpen] = useState(false);

  const openOrderCreationDialog = (props?: typeof orderCreationDialogProps) => {
    setOrderCreationDialogProps({
      services: servicesWithIcons,
      ...props,
    });
    setOrderCreationDialogOpen(true);
  };

  return (
    <>
      <div className="flex h-full flex-col overflow-hidden">
        <header className="px-4 py-1">
          <ul className="mx-auto flex h-full max-w-5xl items-center justify-between gap-2">
            <li className="flex items-center gap-3">
              <Logo />
              <div>
                <h1 className="max-2xs:hidden text-xl font-bold">{COMPANY_INFO.name}</h1>
                <p className="hidden text-sm md:block">
                  {[tc(Tk.ORDER_ACCEPTANCE), COMPANY_INFO.workSchedule].join(COLON_WITH_SPACE)}
                </p>
              </div>
            </li>
            <li>
              <nav>
                <ul className="hidden gap-2 md:flex">
                  {MENU_ITEMS.map(({ labelT, sectionName }) => (
                    <li
                      key={isObject(labelT) ? labelT.key : labelT}
                      className="hover:text-primary-lighter flex"
                    >
                      <a
                        href={[HASH, sectionName].join(EMPTY)}
                        className={cn(styles["navigation-button"])}
                      >
                        {tc(...TranslationParams.toTuple(labelT))}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </li>
            <li>
              <ul className="flex gap-4">
                <li>
                  <LanguageSwitcher.Dropdown />
                </li>
                <li className="list-item md:hidden">
                  <NavigationMenu />
                </li>
              </ul>
            </li>
          </ul>
        </header>
        <div className="relative overflow-auto">
          <main className="flex flex-col">
            <Section
              name={HomeSectionName.introduction}
              className="from-tertiary text-tertiary-foreground bg-linear-to-r to-black py-3 sm:py-6"
              childrenWrapperClassName="items-center justify-evenly gap-8 px-4 lg:flex-row"
            >
              <div className="flex flex-col gap-2 sm:gap-4">
                <h1 className="text-2xl font-bold md:text-5xl">
                  {tc(Tk.PROFESSIONAL_RELOCATION_SERVICES_IN_SWITZERLAND)}
                </h1>
                <p className="sm:text-xl">
                  {tc(Tk.RELIABLE_LOADERS_EXPERIENCED_DRIVERS_AND_THE_BEST_PRICE_ON_THE_MARKET)}
                </p>
                <ul className="flex flex-wrap gap-1">
                  {[
                    {
                      colors: {
                        background: "bg-green-700",
                        foreground: "text-green-100",
                      },
                      textT: Tk.CASH_PAYMENT_IS_POSSIBLE,
                    },
                    {
                      colors: {
                        background: "bg-yellow-700",
                        foreground: "text-yellow-100",
                      },
                      textT: Tk.WE_HAVE_SPECIAL_OFFERS_FOR_SENIORS,
                    },
                    {
                      colors: {
                        background: "bg-pink-700",
                        foreground: "text-pink-100",
                      },
                      textT: Tk.WE_PROVIDE_A_10_DISCOUNT_FOR_STUDENTS,
                    },
                  ].map(({ colors: { background, foreground }, textT }, index) => (
                    <li key={index}>
                      <Badge className={cn(background, foreground)}>{tc(textT)}</Badge>
                    </li>
                  ))}
                </ul>
                <div className="max-xs:flex-col flex gap-2">
                  {(
                    [
                      {
                        onClick: openOrderCreationDialog,
                        children: (
                          <>
                            <Calculator />
                            {tc(Tk.CALCULATE_THE_COST)}
                          </>
                        ),
                      },
                      {
                        asChild: true,
                        variant: "secondary",
                        children: (
                          <a href={[HASH, WRITE_TO_US].join(EMPTY)}>
                            <UserPen />
                            {tc(Tk.WRITE_TO_US)}
                          </a>
                        ),
                      },
                    ] as Partial<ComponentProps<typeof Button>>[]
                  ).map(({ variant, size, className, ...props }, index) => {
                    return (
                      <Button
                        key={index}
                        {...(variant && {
                          variant,
                        })}
                        size={size ?? "auto"}
                        className={cn(
                          "px-4 py-2 sm:px-8 sm:py-4 sm:text-lg sm:[&_svg:not([class*='size-'])]:size-6",
                          className,
                        )}
                        {...props}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="text-primary-foreground">
                <div className="bg-primary-lighter relative m-auto size-32 rounded-full *:flex *:items-center *:justify-center sm:size-40 md:size-64 [&>div]:rounded-full">
                  <Boxes className="absolute top-1/2 left-1/2 size-3/5 -translate-1/2" />
                  {[["24/7", t(Tk.SUPPORT)].join(SPACE), tc(Tk.RELIABLE_SERVICE)].map(
                    (text, index) => (
                      <div
                        key={index}
                        className={cn(
                          "bg-primary text-primary-foreground absolute size-1/3 flex-col",
                          "first-of-type:-top-1/20 first-of-type:-left-1/20",
                          "last-of-type:-right-1/20 last-of-type:-bottom-1/20",
                        )}
                      >
                        {/* <span className="text-sm font-bold sm:text-xl lg:text-4xl">{text1}</span> */}
                        <span className="text-center text-xs sm:text-base lg:text-lg">{text}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </Section>
            <Section
              childrenWrapperClassName="max-w-4xl"
              headerProps={{
                titleT: Tk.WHY_CUSTOMERS_CHOOSE_US,
              }}
            >
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                {OUR_ADVANTAGES.map(({ title, description, Icon }) => (
                  <Card key={title} className="bg-background-secondary transition hover:shadow-lg">
                    <CardHeader className="justify-items-center gap-4 *:text-center">
                      <div className="bg-primary text-primary-foreground flex size-16 rounded-full">
                        <Icon className="m-auto" />
                      </div>
                      <CardTitle>{tc(title)}</CardTitle>
                      <CardDescription>{tc(description)}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </Section>
            <Section
              name={HomeSectionName.services}
              className="bg-background-secondary pb-0 sm:pb-0"
              childrenWrapperClassName="max-w-5xl gap-0 sm:gap-0"
              headerProps={{
                titleT: Tk.OUR_SERVICES,
                subtitleT:
                  Tk.WE_OFFER_A_FULL_RANGE_OF_SERVICES_FOR_MOVING_APARTMENTS_OFFICES_AND_INDUSTRIAL_FACILITIES,
              }}
            >
              {isServicesError ? (
                <QueryError
                  className="pt-(--gap) pb-(--py) sm:pt-(--sm-gap) sm:pb-(--sm-py)"
                  onTryAgain={() => void refetchServices()}
                />
              ) : (
                <Carousel
                  opts={{
                    dragFree: true,
                  }}
                >
                  <CarouselPrevious />
                  <CarouselContent className="pt-(--gap) pb-(--py) sm:pt-(--sm-gap) sm:pb-(--sm-py)">
                    {isServicesPending
                      ? range(3).map((value) => (
                          <CarouselItem key={value} className="sm:basis-1/2 md:basis-1/3">
                            <ServiceCardSkeleton className="h-full shadow-md transition hover:-translate-y-1 hover:shadow-xl" />
                          </CarouselItem>
                        ))
                      : services.map((service) => (
                          <CarouselItem key={service.id} className="sm:basis-1/2 md:basis-1/3">
                            <ServiceCard
                              service={service}
                              className="h-full shadow-md transition hover:-translate-y-1 hover:shadow-xl"
                            />
                          </CarouselItem>
                        ))}
                  </CarouselContent>
                  <CarouselNext />
                </Carousel>
              )}
            </Section>
            <Section
              childrenWrapperClassName="xs:px-4 max-w-5xl md:px-8"
              headerProps={{
                titleT: Tk.MOVING_COST,
                subtitleT: Tk.FLEXIBLE_PRICING_SYSTEM_FOR_DIFFERENT_ORDERS,
              }}
            >
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {(Object.entries(MOVING_PRICES) as Entries<typeof MOVING_PRICES>).map(
                  ([
                    buildingType,
                    {
                      minimumPrice,
                      includedServices: { cars, numberOfLoaders },
                      isPopular,
                      note,
                    },
                  ]) => {
                    const CheckIcon = (
                      <Check
                        className={cn(
                          "shrink-0",
                          isPopular ? "stroke-primary-foreground" : "stroke-success",
                        )}
                      />
                    );

                    return (
                      <Card
                        key={buildingType}
                        className={cn(
                          "relative py-8",
                          isPopular
                            ? "from-primary-lighter text-primary-foreground to-primary bg-linear-to-br md:-translate-y-4"
                            : "bg-background-secondary",
                        )}
                      >
                        {isPopular ? (
                          <Badge className="bg-success absolute top-2 right-2 py-1 text-sm">
                            {tc(Tk.POPULAR)}
                          </Badge>
                        ) : (
                          <div className="bg-primary-lighter absolute top-0 h-2 w-full rounded-t-xl"></div>
                        )}
                        <CardHeader className="gap-4 px-8 *:text-center">
                          <CardTitle className="text-2xl">{tc(buildingType)}</CardTitle>
                          <CardDescription
                            className={cn(
                              "text-4xl font-bold",
                              isPopular && "text-primary-foreground",
                            )}
                          >
                            {t(Tk.FROM_PRICE, {
                              price: formatCurrency(minimumPrice),
                            })}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-8 px-8">
                          <ul>
                            <li className="flex justify-center gap-2">
                              {CheckIcon}
                              {capitalize(
                                new Intl.ListFormat(i18n.language, {
                                  style: "long",
                                  type: "disjunction",
                                }).format(
                                  cars.map((car) =>
                                    car.type === "CARGO_GAZELLE"
                                      ? t(Tk.CARGO_GAZELLE)
                                      : [
                                          t(Tk.TRUCK),
                                          [
                                            Array.isArray(car.loadCapacity)
                                              ? [car.loadCapacity[0], car.loadCapacity[1]].join(
                                                  DASH,
                                                )
                                              : car.loadCapacity,
                                            t(Tk.T),
                                          ].join(EMPTY),
                                        ].join(SPACE),
                                  ),
                                ),
                              )}
                            </li>
                            <li className="flex justify-center gap-2">
                              {CheckIcon}
                              {[
                                tc(Tk.NUMBER_OF_LOADERS),

                                Array.isArray(numberOfLoaders)
                                  ? [numberOfLoaders[0], numberOfLoaders[1]].join(DASH)
                                  : numberOfLoaders,
                              ].join(COLON_WITH_SPACE)}
                            </li>
                            {[Tk.LOADING_UNLOADING, Tk.TRANSPORTATION].map((value, index) => (
                              <li key={index} className="flex justify-center gap-2">
                                {CheckIcon}
                                {tc(value)}
                              </li>
                            ))}
                          </ul>
                          <p className="text-center">{tc(note)}</p>
                        </CardContent>
                        <CardFooter>
                          <Button
                            size="xl"
                            className={cn(
                              "w-full",
                              isPopular &&
                                "text-primary bg-primary-foreground hover:bg-primary-foreground/90",
                            )}
                            onClick={() =>
                              openOrderCreationDialog({
                                defaultValues: {
                                  from: {
                                    buildingType,
                                  },
                                  to: {
                                    buildingType,
                                  },
                                },
                              })
                            }
                          >
                            {tc(Tk.ORDER, {
                              context: PartOfSpeech.verb,
                            })}
                          </Button>
                        </CardFooter>
                      </Card>
                    );
                  },
                )}
              </div>
            </Section>
            <Section
              id={HomeSectionName.calculator}
              className="bg-background-secondary"
              childrenWrapperClassName="mx-6 max-w-5xl sm:mx-12 lg:mx-auto"
              headerProps={{
                titleT: Tk.COST_CALCULATOR,
                subtitleT: Tk.CALCULATE_THE_COST_OF_YOUR_MOVE,
              }}
            >
              {isServicesError ? (
                <QueryError onTryAgain={() => void refetchServices()} />
              ) : isServicesPending ? (
                <CalculatorCardSkeleton />
              ) : (
                <CalculatorCard
                  services={servicesWithIcons}
                  moreInfoButton={(service) => (
                    <ServiceDetailsDialog
                      service={service}
                      triggerProps={{
                        asChild: true,
                        children: (
                          <Button variant="ghost" size="auto" className="size-6">
                            <CircleQuestionMark />
                          </Button>
                        ),
                      }}
                    />
                  )}
                  buttonToOrder={(button, services, selectedServices) =>
                    cloneElement(button, {
                      onClick: combine([
                        (event) => button.props.onClick?.(event),
                        () => {
                          setOrderCreationDialogProps({
                            services,
                            selectedServices: Object.entries(selectedServices)
                              .map(([, services]) => services)
                              .flat()
                              .filter(({ count }) => count > 0),
                          });
                          setOrderCreationDialogOpen(true);
                        },
                      ]),
                    })
                  }
                />
              )}
            </Section>
            <Section
              id={HomeSectionName.reviews}
              childrenWrapperClassName="max-w-4xl max-lg:mx-8"
              headerProps={{
                titleT: Tk.OUR_CUSTOMERS_REVIEWS,
              }}
            >
              <ReviewsSectionContent />
            </Section>
            <Section
              childrenWrapperClassName="max-w-4xl"
              headerProps={{
                titleT: () => ts(QUESTION_MARK)(Tk.HOW_DO_WE_WORK),
              }}
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {[
                  {
                    title: tc(Tk.ORDER),
                    description: tc(
                      Tk.YOU_LEAVE_A_REQUEST_ON_THE_WEBSITE_OR_CALL_OUR_MANAGER_WILL_CONTACT_YOU_WITHIN_15_MINUTES,
                    ),
                  },
                  {
                    title: tc(Tk.PREPARATION, {
                      context: PartOfSpeech.noun,
                    }),
                    description: tc(
                      Tk.WE_CALCULATE_THE_COST_AGREE_ON_A_TIME_BRING_PACKING_MATERIALS_AND_PREPARE_THE_TEAM,
                    ),
                  },
                  {
                    title: tc(Tk.MOVING),
                    description: tc(
                      Tk.OUR_SPECIALISTS_ARRIVE_AT_THE_APPOINTED_TIME_CAREFULLY_PACK_LOAD_AND_TRANSPORT_YOUR_BELONGINGS,
                    ),
                  },
                ].map(({ title, description }, index) => (
                  <button key={index} type="button" className="group relative perspective-midrange">
                    <div className="h-full transition duration-500 transform-3d [@media(hover:hover)]:group-hover:rotate-y-180 [@media(hover:none)]:group-focus:rotate-y-180">
                      <Card className="to-primary-lighter from-primary bg-linear-to-br py-8 backface-hidden">
                        <CardHeader className="*:font-roboto *:text-primary-foreground px-8 *:text-center">
                          <CardTitle className="text-4xl">{index + 1}</CardTitle>
                          <CardDescription className="text-xl font-bold">{title}</CardDescription>
                        </CardHeader>
                      </Card>
                      <Card className="border-primary rotate-y-180 border-2 py-8 backface-hidden">
                        <CardContent className="text-foreground px-8 text-center">
                          {description}
                        </CardContent>
                      </Card>
                    </div>
                  </button>
                ))}
              </div>
            </Section>
            <Section
              childrenWrapperClassName="max-w-4xl max-lg:mx-8"
              headerProps={{
                titleT: Tk.ABOUT_US,
              }}
            >
              <AboutUsSection />
            </Section>
            <Section
              id={HomeSectionName.contacts}
              className="bg-background-secondary"
              childrenWrapperClassName="max-w-5xl"
              headerProps={{
                titleT: {
                  key: Tk.CONTACT,
                  options: {
                    count: 2,
                  },
                },
              }}
            >
              <div className="flex flex-col shadow-xl md:flex-row">
                <Card className="from-primary-lighter to-primary text-primary-foreground border-0 bg-linear-to-b py-8 shadow-none max-md:rounded-b-none md:w-1/3 md:rounded-r-none">
                  <CardHeader className="px-8">
                    <CardTitle className="text-2xl font-bold">{tc(Tk.CONTACT_US)}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-8">
                    <ul className="flex flex-col gap-6">
                      {(
                        [
                          {
                            Icon: MapPin,
                            contentType: [
                              tc(Tk.ADDRESS, {
                                count: 1,
                              }),
                              COLON,
                            ].join(EMPTY),
                            content: <address>{COMPANY_INFO.address}</address>,
                          },
                          {
                            Icon: Phone,
                            contentType: [
                              tc(Tk.PHONE, {
                                count: 1,
                              }),
                              COLON,
                            ].join(EMPTY),
                            content: (
                              <a href={COMPANY_INFO_CONTACT_LINKS.phone}>{COMPANY_INFO.phone}</a>
                            ),
                          },
                          {
                            Icon: Mail,
                            contentType: [tc(Tk.EMAIL), COLON].join(EMPTY),
                            content: (
                              <a href={COMPANY_INFO_CONTACT_LINKS.email}>{COMPANY_INFO.email}</a>
                            ),
                          },
                          {
                            Icon: Clock,
                            contentType: [tc(Tk.WORK_SCHEDULE), COLON].join(EMPTY),
                            content: [
                              [
                                getNameOfDayOfWeek(i18n.language, DayOfWeek.MONDAY),
                                getNameOfDayOfWeek(i18n.language, DayOfWeek.SUNDAY),
                              ]
                                .map((value) => capitalize(value))
                                .join(DASH),
                              COMPANY_INFO.workSchedule,
                            ].join(COLON_WITH_SPACE),
                          },
                        ] as {
                          Icon: LucideIcon;
                          contentType: string;
                          content: string;
                        }[]
                      ).map(({ Icon, contentType, content }, index) => (
                        <li key={index} className="flex flex-col gap-2">
                          <span className="flex gap-3 font-bold">
                            <Icon />
                            {contentType}
                          </span>
                          {content}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                <Card
                  id={WRITE_TO_US}
                  className="border-0 py-8 shadow-none max-md:rounded-t-none md:w-2/3 md:rounded-l-none"
                >
                  <CardHeader className="px-8">
                    <CardTitle className="text-2xl font-bold">{tc(Tk.WRITE_TO_US)}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-8">
                    <WriteToUsForm />
                  </CardContent>
                </Card>
              </div>
            </Section>
          </main>
          <footer className="text-tertiary-foreground bg-tertiary flex flex-col gap-8 px-4 py-6 sm:py-12">
            <div className="[&_h3,h4]:text-tertiary-foreground mx-auto grid max-w-4xl grid-cols-1 gap-8 text-gray-400 md:grid-cols-2 md:max-lg:justify-items-center lg:grid-cols-4 [&_h3,h4]:text-xl [&_h3,h4]:font-bold [&>*]:flex [&>*]:flex-col [&>*]:gap-4">
              <div className="md:max-lg:max-w-64">
                <div className="flex items-center gap-3">
                  <Logo />
                  <h3>{COMPANY_INFO.name}</h3>
                </div>
                <p>
                  {[
                    tsp(Tk.PROFESSIONAL_RELOCATION_SERVICES_IN_SWITZERLAND),
                    tsp(Tk.CONVENIENT_FAST_RELIABLE),
                  ].join(SPACE)}
                </p>
              </div>
              <div>
                <h4>
                  {tc(Tk.SERVICE, {
                    count: 2,
                  })}
                </h4>
                <ul className="flex flex-col gap-2">
                  {[
                    tc(Tk.APARTMENT_MOVING),
                    tc(Tk.OFFICE_RELOCATION),
                    tc(Tk.INDUSTRIAL_RELOCATION),
                    tc(Tk.PACKING_THINGS),
                    tc(Tk.TRANSPORTATION),
                  ].map((value, index) => (
                    <li key={index}>{value}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>
                  {tc(Tk.CONTACT, {
                    count: 2,
                  })}
                </h4>
                <ul className="flex flex-col gap-2">
                  {[
                    {
                      Icon: MapPin,
                      text: <address>{COMPANY_INFO.address}</address>,
                    },
                    ...[
                      {
                        Icon: Phone,
                        text: COMPANY_INFO.phone,
                        href: COMPANY_INFO_CONTACT_LINKS.phone,
                      },
                      {
                        Icon: Mail,
                        text: COMPANY_INFO.email,
                        href: COMPANY_INFO_CONTACT_LINKS.email,
                      },
                    ].map(({ Icon, text, href }) => ({
                      Icon,
                      text: (
                        <a href={href} className="hover:text-tertiary-foreground">
                          {text}
                        </a>
                      ),
                    })),
                  ].map(({ Icon, text }, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Icon className="shrink-0" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src={qrCode}
                  alt={t(Tk.QR_CODE_TO_GO_TO_THIS_SITE)}
                  className="size-48 rounded-lg max-md:m-auto lg:m-auto"
                />
              </div>
            </div>
            <Separator className="bg-gray-700" />
            <p className="text-center text-gray-400">
              {[
                COPYRIGHT,
                new Date().getFullYear(),
                [COMPANY_INFO.name, PERIOD].join(EMPTY),
                tsp(Tk.ALL_RIGHTS_RESERVED),
              ].join(SPACE)}
            </p>
          </footer>
        </div>
        <MyOrders />
        <ConsentSummary />
      </div>
      <OrderCreationDialog
        onSuccess={(order) => {
          setOrderCreationDialogOpen(false);
          MyOrdersStore.add(order);
          eventBus.emit("customerCreatedOrder", order);
          void navigate({
            to: "/{-$locale}/order-printout/$orderId",
            params: {
              orderId: order.id,
            },
            search: {
              retrieveFrom: RetrieveFrom.LOCAL_STORAGE,
            },
          });
        }}
        open={isOrderCreationDialogOpen}
        onOpenChange={setOrderCreationDialogOpen}
        showAlertDialogBeforeReset
        contentProps={{
          className: "max-h-[calc(100%-2rem)]",
        }}
        {...orderCreationDialogProps}
      />
    </>
  );
}

function NavigationMenu() {
  const {
    postproc: { tc },
  } = useTranslation2();
  const [isOpen, setOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={tc(isOpen ? Tk.CLOSE_NAVIGATION_MENU : Tk.OPEN_NAVIGATION_MENU)}
        >
          <Menu />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {MENU_ITEMS.map(({ labelT, sectionName }) => (
          <DropdownMenuItem key={isObject(labelT) ? labelT.key : labelT} asChild>
            <a href={[HASH, sectionName].join(EMPTY)}>{tc(...TranslationParams.toTuple(labelT))}</a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ICONS = [House, Building, Factory];

const SECTION_OPTIONS = {
  py: {
    default: "--py",
    sm: "--sm-py",
  },
  gap: {
    default: "--gap",
    sm: "--sm-gap",
  },
};

type SectionProps = ComponentProps<"section"> & {
  name?: HomeSectionName | undefined;
  childrenWrapperClassName?: ComponentProps<"div">["className"] | undefined;
  headerProps?: SectionHeaderProps | undefined;
};

function Section({
  name,
  id,
  className,
  childrenWrapperClassName,
  headerProps,
  children,
  ...props
}: SectionProps) {
  return (
    <section
      id={id ?? name}
      style={
        {
          [SECTION_OPTIONS.py.default]: "calc(var(--spacing) * 8)",
          [SECTION_OPTIONS.py.sm]: "calc(var(--spacing) * 20)",
          [SECTION_OPTIONS.gap.default]: "calc(var(--spacing) * 8)",
          [SECTION_OPTIONS.gap.sm]: "calc(var(--spacing) * 16)",
        } as CSSProperties
      }
      className={cn("py-(--py) sm:py-(--sm-py)", className)}
      {...props}
    >
      <div
        className={cn(
          "mx-auto flex flex-col gap-(--gap) sm:gap-(--sm-gap)",
          childrenWrapperClassName,
        )}
      >
        {headerProps && <SectionHeader {...headerProps} />}
        {children}
      </div>
    </section>
  );
}

type SectionHeaderProps = Omit<ComponentProps<"div">, "children"> & {
  titleT: TranslationParams.Type | (() => string);
  subtitleT?: TranslationParams.Type | undefined;
};

function SectionHeader({ titleT, subtitleT, className }: SectionHeaderProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <div>
      <h2
        className={cn(
          "font-montserrat text-center text-xl font-bold sm:text-3xl",
          subtitleT && "mb-2 sm:mb-6",
          className,
        )}
      >
        {typeof titleT === "function" ? titleT() : tc(...TranslationParams.toTuple(titleT))}
      </h2>
      {subtitleT && (
        <p className="text-center text-sm sm:text-xl">
          {tc(...TranslationParams.toTuple(subtitleT))}
        </p>
      )}
    </div>
  );
}

type MenuItem = {
  labelT: TranslationParams.Type;
  sectionName: HomeSectionName;
};

const MENU_ITEMS: MenuItem[] = [
  {
    labelT: Tk.INTRODUCTION,
    sectionName: HomeSectionName.introduction,
  },
  {
    labelT: {
      key: Tk.SERVICE,
      options: {
        count: 2,
      },
    },
    sectionName: HomeSectionName.services,
  },
  {
    labelT: Tk.CALCULATOR,
    sectionName: HomeSectionName.calculator,
  },
  {
    labelT: {
      key: Tk.REVIEW,
      options: {
        count: 2,
      },
    },
    sectionName: HomeSectionName.reviews,
  },
  {
    labelT: {
      key: Tk.CONTACT,
      options: {
        count: 2,
      },
    },
    sectionName: HomeSectionName.contacts,
  },
];

type AboutUsChapter = {
  title: Tk;
  paragraphs: Tk[];
};

const ABOUT_US_CHAPTERS: AboutUsChapter[] = [
  {
    title: Tk.ABOUT_US_CHAPTER_1_TITLE,
    paragraphs: [Tk.ABOUT_US_CHAPTER_1_PARAGRAPH],
  },
  {
    title: Tk.ABOUT_US_CHAPTER_2_TITLE,
    paragraphs: [Tk.ABOUT_US_CHAPTER_2_PARAGRAPH_1, Tk.ABOUT_US_CHAPTER_2_PARAGRAPH_2],
  },
  {
    title: Tk.ABOUT_US_CHAPTER_3_TITLE,
    paragraphs: [Tk.ABOUT_US_CHAPTER_3_PARAGRAPH_1, Tk.ABOUT_US_CHAPTER_3_PARAGRAPH_2],
  },
  {
    title: Tk.ABOUT_US_CHAPTER_4_TITLE,
    paragraphs: [Tk.ABOUT_US_CHAPTER_4_PARAGRAPH_1, Tk.ABOUT_US_CHAPTER_4_PARAGRAPH_2],
  },
];

function AboutUsSection() {
  const {
    translation: { t },
  } = useTranslation2();

  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4">
      {ABOUT_US_CHAPTERS.map(({ title, paragraphs }, chapterIndex) => (
        <Card key={chapterIndex} className="max-md:gap-4 max-md:py-4">
          <CardHeader className="block max-md:px-4">
            <CardTitle>{t(title)}</CardTitle>
          </CardHeader>
          <CardContent className="max-md:px-4">
            {paragraphs.map((paragraph, paragraphIndex) => (
              <p key={paragraphIndex}>
                <Trans i18nKey={paragraph as string} />
              </p>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const WRITE_TO_US = "write-to-us";

type OurAdvantage = {
  title: Tk;
  description: Tk;
  Icon: LucideIcon;
};

const OUR_ADVANTAGES: OurAdvantage[] = [
  {
    title: Tk.FAVORABLE_PRICES,
    description: Tk.THE_BEST_PRICES_ON_THE_MARKET_WITHOUT_HIDDEN_FEES,
    Icon: Wallet,
  },
  {
    title: Tk.FAST_CAR_DELIVERY,
    description: Tk.TRANSPORT_WILL_ARRIVE_WITHIN_30_60_MINUTES,
    Icon: Truck,
  },
  {
    title: Tk.EXPERIENCED_LOADERS,
    description: Tk.QUALIFIED_EMPLOYEES_WITH_EXTENSIVE_WORK_EXPERIENCE,
    Icon: BicepsFlexed,
  },
  {
    title: Tk.GUARANTEE_OF_PRESERVATION,
    description: Tk.YOUR_BELONGINGS_ARE_SAFE_THROUGHOUT_THE_MOVE,
    Icon: ShieldHalf,
  },
];

type MovingPrice = {
  buildingType: OrderEntity.BuildingTypeSchema;
  minimumPrice: number;
  includedServices: Services;
  isPopular: boolean;
  note: Tk;
};

type Services = {
  cars: CarAsService[];
  numberOfLoaders: number | ReadonlyTuple<number, 2>;
};

type CarAsService =
  | {
      type: "CARGO_GAZELLE";
    }
  | {
      type: "TRUCK";
      loadCapacity: number | ReadonlyTuple<number, 2>;
    };

const MOVING_PRICES: Record<MovingPrice["buildingType"], Omit<MovingPrice, "buildingType">> = {
  APARTMENT: {
    minimumPrice: 540,
    includedServices: {
      cars: [
        {
          type: "CARGO_GAZELLE",
        },
        {
          type: "TRUCK",
          loadCapacity: 3.5,
        },
      ],
      numberOfLoaders: 2,
    },
    isPopular: false,
    note: Tk.PRICE_FOR_A_1_ROOM_APARTMENT,
  },
  PRIVATE_HOUSE: {
    minimumPrice: 1500,
    includedServices: {
      cars: [
        {
          type: "TRUCK",
          loadCapacity: 5,
        },
      ],

      numberOfLoaders: [3, 4],
    },
    isPopular: true,
    note: Tk.PRICE_FOR_A_HOUSE_WITH_AN_AREA_OF_UP_TO_100_M,
  },
  OFFICE: {
    minimumPrice: 1250,
    includedServices: {
      cars: [
        {
          type: "TRUCK",
          loadCapacity: [3.5, 5],
        },
      ],

      numberOfLoaders: [3, 5],
    },
    isPopular: false,
    note: Tk.PRICE_FOR_AN_OFFICE_WITH_AN_AREA_OF_UP_TO_50_M,
  },
};

export { HomePage };
